import { motion } from 'framer-motion';
import { FileText, Globe, Lock, MessageCircle, Zap, Database, Bot } from 'lucide-react';
import { SpotlightCard } from './ui/SpotlightCard';

export function Features({ t }: { t: any }) {
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <section id="features" className="bg-zinc-950 py-24 sm:py-32 relative overflow-hidden">
      {/* Decorative background glow */}
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-[500px] h-[500px] bg-emerald-500/5 blur-[100px] rounded-full pointer-events-none" />

      <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        <div className="mx-auto max-w-2xl lg:text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-base font-semibold leading-7 text-emerald-400">{t.features.section_title}</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              {t.features.main_title}
            </p>
            <p className="mt-6 text-lg leading-8 text-zinc-400">
              {t.features.description}
            </p>
          </motion.div>
        </div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
          className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none"
        >
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:grid-rows-2 h-full">
            
            {/* Card 1: RAG Big */}
            <motion.div variants={itemVariants} className="lg:col-span-2 lg:row-span-2 h-full">
              <SpotlightCard className="h-full p-8">
                <div className="absolute top-0 right-0 -mt-20 -mr-20 w-[300px] h-[300px] bg-emerald-500/10 blur-[80px] rounded-full pointer-events-none" />
                <div className="relative z-10 h-full flex flex-col justify-between">
                  <div>
                     <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/10 mb-4 ring-1 ring-inset ring-emerald-500/20">
                        <Database className="h-6 w-6 text-emerald-500" />
                     </div>
                     <h3 className="text-xl font-semibold text-white mb-2">{t.features.cards.rag.title}</h3>
                     <p className="text-zinc-400">
                        {t.features.cards.rag.desc}
                     </p>
                  </div>
                  
                  {/* Animated Data Hub Visualization */}
                  <div className="mt-8 relative w-full h-64 bg-zinc-900/50 rounded-xl border border-white/5 overflow-hidden group">
                     {/* Background Grid */}
                     <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:32px_32px]" />
                     <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent" />

                     {/* Central Core */}
                     <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                        <div className="relative flex items-center justify-center">
                           {/* Pulsing rings */}
                           <div className="absolute w-24 h-24 bg-emerald-500/20 rounded-full animate-ping opacity-20" />
                           <div className="absolute w-32 h-32 bg-emerald-500/10 rounded-full animate-[pulse_3s_infinite]" />
                           {/* Core Icon */}
                           <div className="relative z-10 w-16 h-16 bg-zinc-900 border border-emerald-500/30 rounded-full flex items-center justify-center shadow-[0_0_30px_-5px_rgba(16,185,129,0.3)]">
                              <Bot className="w-8 h-8 text-emerald-400" />
                           </div>
                        </div>
                     </div>

                     {/* Orbiting Sources */}
                     {/* Source 1: PDF (Top Left) */}
                     <motion.div 
                        initial={{ opacity: 0, scale: 0 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2 }}
                        className="absolute top-8 left-8 sm:left-16 p-3 bg-zinc-800/80 backdrop-blur-md border border-white/10 rounded-xl flex items-center gap-3 shadow-lg"
                     >
                        <div className="p-1.5 bg-red-500/20 rounded-lg">
                           <FileText className="w-4 h-4 text-red-400" />
                        </div>
                        <div className="flex flex-col">
                           <span className="text-xs font-medium text-zinc-200">Report.pdf</span>
                           <span className="text-[10px] text-emerald-400 flex items-center gap-1">
                              <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" /> Synced
                           </span>
                        </div>
                        {/* Connection Beam */}
                        <div className="absolute top-1/2 right-0 w-24 h-[1px] bg-gradient-to-r from-zinc-700 to-emerald-500/0 origin-left rotate-[25deg] opacity-30 -z-10" />
                     </motion.div>

                     {/* Source 2: Notion (Bottom Right) */}
                     <motion.div 
                        initial={{ opacity: 0, scale: 0 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4 }}
                        className="absolute bottom-8 right-8 sm:right-16 p-3 bg-zinc-800/80 backdrop-blur-md border border-white/10 rounded-xl flex items-center gap-3 shadow-lg"
                     >
                        <div className="p-1.5 bg-blue-500/20 rounded-lg">
                           <Database className="w-4 h-4 text-blue-400" />
                        </div>
                        <div className="flex flex-col">
                           <span className="text-xs font-medium text-zinc-200">Wiki Notion</span>
                           <span className="text-[10px] text-zinc-500">Indexing...</span>
                        </div>
                     </motion.div>

                     {/* Source 3: Web (Top Right) - Floating animation */}
                     <motion.div 
                        animate={{ y: [0, -10, 0] }}
                        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute top-12 right-8 sm:right-12 p-2 bg-zinc-800/50 border border-white/5 rounded-lg"
                     >
                        <Globe className="w-5 h-5 text-purple-400" />
                     </motion.div>

                     {/* Active Particles (Data Flow) */}
                     {/* Particle from PDF to Core */}
                     <div className="absolute top-[60px] left-[140px] sm:left-[180px]">
                        <motion.div
                           animate={{ 
                              x: [0, 100],
                              y: [0, 60],
                              opacity: [0, 1, 0],
                              scale: [0.5, 1, 0.5]
                           }}
                           transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                           className="w-2 h-2 bg-emerald-400 rounded-full shadow-[0_0_10px_rgba(16,185,129,0.8)]"
                        />
                     </div>
                  </div>
                </div>
              </SpotlightCard>
            </motion.div>

            {/* Card 2: WhatsApp */}
            <motion.div variants={itemVariants} className="h-full">
              <SpotlightCard className="h-full p-8">
                 <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10 mb-4 ring-1 ring-inset ring-green-500/20">
                    <MessageCircle className="h-6 w-6 text-green-500" />
                 </div>
                 <h3 className="text-xl font-semibold text-white mb-2">{t.features.cards.whatsapp.title}</h3>
                 <p className="text-sm text-zinc-400">
                    {t.features.cards.whatsapp.desc}
                 </p>
                 {/* Subtle animation */}
                 <div className="absolute bottom-4 right-4 opacity-10">
                    <MessageCircle className="w-16 h-16 rotate-12" />
                 </div>
              </SpotlightCard>
            </motion.div>

            {/* Card 3: Security */}
            <motion.div variants={itemVariants} className="h-full">
              <SpotlightCard className="h-full p-8" spotlightColor="rgba(255, 255, 255, 0.1)">
                 <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-zinc-800 mb-4 ring-1 ring-inset ring-white/10">
                    <Lock className="h-6 w-6 text-zinc-300" />
                 </div>
                 <h3 className="text-xl font-semibold text-white mb-2">{t.features.cards.security.title}</h3>
                 <p className="text-sm text-zinc-400">
                    {t.features.cards.security.desc}
                 </p>
                 <div className="absolute bottom-4 right-4 opacity-10">
                    <Lock className="w-16 h-16 -rotate-12" />
                 </div>
              </SpotlightCard>
            </motion.div>
          </div>

          {/* Bottom Row Small Features */}
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Zap, label: t.features.badges.speed, color: "text-yellow-400", bg: "bg-yellow-400/10", border: "border-yellow-400/20" },
              { icon: Globe, label: t.features.badges.languages, color: "text-blue-400", bg: "bg-blue-400/10", border: "border-blue-400/20" },
              { icon: Bot, label: t.features.badges.models, color: "text-purple-400", bg: "bg-purple-400/10", border: "border-purple-400/20" },
              { icon: FileText, label: t.features.badges.citations, color: "text-red-400", bg: "bg-red-400/10", border: "border-red-400/20" },
            ].map((item, idx) => (
              <motion.div 
                key={idx} 
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                className={`flex items-center gap-3 rounded-2xl p-4 border ${item.border} bg-zinc-900/50 backdrop-blur-sm transition-colors hover:bg-zinc-900`}
              >
                <div className={`p-2 rounded-lg ${item.bg}`}>
                  <item.icon className={`h-4 w-4 ${item.color}`} />
                </div>
                <span className="font-medium text-white text-sm">{item.label}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
